'use client';

type Guests = { adults: number; children: number; childAges: number[] };

/** Participant counters deliberately have no room/bed limits. */
export default function DailyTourGuests({ value, onChange, t, minChildAge = 0 }: {
    value: Guests;
    onChange: (value: Guests) => void;
    t: (key: string, fallback?: string) => string;
    minChildAge?: number;
}) {
    const counter = (label: string, count: number, min: number, change: (count: number) => void) => (
        <div className="flex items-center justify-between gap-3">
            <span className="font-medium text-alsero-dark-gray">{label}</span>
            <div className="flex items-center gap-3">
                <button type="button" disabled={count <= min} onClick={() => change(count - 1)} className="h-9 w-9 rounded border border-gray-300 disabled:opacity-30">
                    <span className="sr-only">{t('reservation.decrease', 'Decrease')} {label}</span>
                    <span aria-hidden="true">−</span>
                </button>
                <span className="min-w-6 text-center" aria-live="polite">{count}</span>
                <button type="button" disabled={count >= 1000} onClick={() => change(count + 1)} className="h-9 w-9 rounded border border-gray-300 disabled:opacity-30">
                    <span className="sr-only">{t('reservation.increase', 'Increase')} {label}</span>
                    <span aria-hidden="true">+</span>
                </button>
            </div>
        </div>
    );
    return (
        <fieldset className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
            <legend className="px-1 text-sm font-medium">{t('reservation.number_of_guests', 'Number of Guests')}</legend>
            {counter(t('reservation.adults', 'Adults'), value.adults, 1, (adults) => onChange({ ...value, adults }))}
            {counter(t('reservation.children', 'Children'), value.children, 0, (children) => onChange({
                ...value, children,
                childAges: Array.from({ length: children }, (_, i) => value.childAges[i] ?? minChildAge),
            }))}
            {value.childAges.map((age, index) => (
                <label key={index} className="flex items-center justify-between gap-3 text-sm">
                    <span>{t('reservation.child_age', 'Child age')} {index + 1}</span>
                    <select value={age} onChange={(event) => onChange({ ...value, childAges: value.childAges.map((a, i) => i === index ? Number(event.target.value) : a) })} className="rounded border border-gray-300 bg-white p-2">
                        {Array.from({ length: 18 - minChildAge }, (_, i) => i + minChildAge).map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                </label>
            ))}
        </fieldset>
    );
}
